<template>
  <div class="circular-board">
    <svg 
      :width="svgSize" 
      :height="svgSize" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- Main circle outline -->
      <circle 
        :cx="centerPoint" 
        :cy="centerPoint" 
        :r="boardRadius" 
        fill="transparent" 
        stroke="black"
        stroke-width="2"
      />
      
      <!-- Optional: Grid lines for the circle if needed -->
      <g v-if="showGrid">
        <!-- Radial lines -->
        <line 
          v-for="i in gridDivisions" 
          :key="`radial-${i}`"
          :x1="centerPoint"
          :y1="centerPoint"
          :x2="centerPoint + boardRadius * Math.cos(2 * Math.PI * i / gridDivisions)"
          :y2="centerPoint + boardRadius * Math.sin(2 * Math.PI * i / gridDivisions)"
          stroke="rgba(0,0,0,0.3)"
          stroke-width="1"
          stroke-dasharray="5,5"
        />
        
        <!-- Concentric circles -->
        <circle 
          v-for="i in concentricCircles" 
          :key="`concentric-${i}`"
          :cx="centerPoint" 
          :cy="centerPoint" 
          :r="(boardRadius * i) / concentricCircles" 
          fill="transparent" 
          stroke="rgba(0,0,0,0.3)"
          stroke-width="1"
          stroke-dasharray="5,5"
        />
      </g>
      
      <!-- Obstacles if any -->
      <g v-if="hasObstacles">
        <circle 
          v-for="obstacle in obstaclePositions" 
          :key="`obstacle-${obstacle.id}`"
          :cx="obstacle.x" 
          :cy="obstacle.y" 
          :r="cellRadius"
          fill="rgba(128,128,128,0.5)"
          stroke="black"
          stroke-width="1"
        />
      </g>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'CircularBoard',
  props: {
    width: { type: Number, default: 8 },
    cellColor: { type: String, default: 'transparent' },
    obstacles: { type: Array, default: () => [] },
    showGrid: { type: Boolean, default: false }
  },
  computed: {
    svgSize() {
      return Math.max(300, this.width * 40); // Ensure minimum size
    },
    centerPoint() {
      return this.svgSize / 2;
    },
    boardRadius() {
      return this.centerPoint - 10;
    },
    cellRadius() {
      return this.boardRadius / (this.width * 2);
    },
    gridDivisions() {
      return this.width * 2; // Number of radial lines
    },
    concentricCircles() {
      return this.width - 1; // Number of concentric circles (excluding the outer one)
    },
    hasObstacles() {
      return this.obstacles && this.obstacles.length > 0;
    },
    obstaclePositions() {
      if (!this.hasObstacles) return [];
      
      return this.obstacles.map(obstacle => {
        // Convert obstacle coordinates to positions on the circle
        const angle = (2 * Math.PI * obstacle.x) / this.gridDivisions;
        const dist = (obstacle.y + 1) * (this.boardRadius / this.width);
        
        return {
          id: `obstacle-${obstacle.x}-${obstacle.y}`,
          x: this.centerPoint + dist * Math.cos(angle),
          y: this.centerPoint + dist * Math.sin(angle),
          row: obstacle.x,
          col: obstacle.y
        };
      });
    }
  },
  methods: {
    isObstacle(x, y) {
      return this.obstacles.some(
        obstacle => obstacle.x === x && obstacle.y === y
      );
    }
  }
}
</script>

<style scoped>
.circular-board {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>